/**
 * Paginate the collection grid without reloading the page.
 *
 * Clicking a page number normally navigates, which drops the customer back at the top of the
 * document — past the hero, the banners and everything above the grid. This fetches the target
 * page, lifts its <results-list> out of the response and swaps it in, so only the grid and its
 * pagination change.
 *
 * Swapping the whole <results-list> rather than its inner HTML is deliberate: it is a custom
 * element whose base class collects its refs when it connects. Replacing the element runs
 * disconnect and connect properly, so the refs, the filters and the card components all rebuild
 * themselves. Replacing only the inside would leave the component holding references to nodes that
 * are no longer in the document.
 *
 * Opt-in per section. Without the attribute this file does nothing, so collections that want the
 * ordinary full-page behaviour keep it.
 */
(function () {
  const ROOT = 'results-list[data-vp-ajax-pagination]';

  /** Only take over a plain left-click; anything else should behave like a normal link. */
  function isPlainClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey &&
      !event.defaultPrevented
    );
  }

  function sectionIdOf(root) {
    return root.getAttribute('section-id');
  }

  /**
   * The element that actually scrolls. This theme puts `overflow-y: scroll` on body, which makes
   * body the scroll container — `window.scrollTo` then moves nothing at all. Checked at call time
   * rather than assumed, so this still works if that ever changes.
   */
  function scroller() {
    const body = document.body;
    const overflows = /(auto|scroll)/.test(getComputedStyle(body).overflowY);
    if (overflows && body.scrollHeight > body.clientHeight + 10) return body;
    return document.scrollingElement || document.documentElement;
  }

  /** Scroll to the top of the results, not the top of the page. */
  function scrollToResults(root) {
    const anchor = root.querySelector('#ResultsList') || root;
    const offset = Number(root.dataset.vpAjaxPaginationOffset || 0);
    const el = scroller();
    const top = anchor.getBoundingClientRect().top + el.scrollTop - offset;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';

    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: Math.max(0, top), behavior });
    } else {
      el.scrollTop = Math.max(0, top);
    }
  }

  /**
   * Take only the page number from the link and apply it to the URL currently showing, so the
   * active filters and sort survive. The links in a filtered grid do not always carry the filter
   * params, and following one verbatim silently drops the customer's filters.
   */
  function pageUrl(target) {
    const url = new URL(window.location.href);
    const page = target.searchParams.get('page');
    if (page) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    return url;
  }

  async function loadPage(root, url, { push = true } = {}) {
    root.setAttribute('aria-busy', 'true');

    let replacement;
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const id = sectionIdOf(root);
      replacement =
        parsed.querySelector(`results-list[section-id="${id}"]`) || parsed.querySelector('results-list');
      if (!replacement) throw new Error('no results-list in the response');
    } catch (error) {
      // Anything unexpected falls back to a normal navigation rather than leaving the grid stale.
      root.removeAttribute('aria-busy');
      window.location.assign(url);
      return;
    }

    root.replaceWith(replacement);
    if (push) history.pushState({ vpAjaxPagination: true }, '', url);
    scrollToResults(replacement);
    bind(replacement);

    replacement.dispatchEvent(
      new CustomEvent('vp:collection-page', { bubbles: true, detail: { url: String(url) } })
    );
  }

  function bind(root) {
    if (root.dataset.vpAjaxPaginationBound === 'true') return;
    root.dataset.vpAjaxPaginationBound = 'true';

    root.addEventListener('click', (event) => {
      const link = event.target.closest('.pagination a[href]');
      if (!link || !root.contains(link)) return;
      if (link.classList.contains('pagination__link--disabled')) return;
      if (!isPlainClick(event)) return;

      // Only same-origin collection URLs; anything else is left to the browser.
      let target;
      try {
        target = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (target.origin !== window.location.origin) return;

      event.preventDefault();
      loadPage(root, pageUrl(target));
    });
  }

  function bindAll() {
    document.querySelectorAll(ROOT).forEach(bind);
  }

  // Back and forward should move through the pages that were visited, not reload the document.
  window.addEventListener('popstate', (event) => {
    if (!event.state || !event.state.vpAjaxPagination) return;
    const root = document.querySelector(ROOT);
    if (root) loadPage(root, window.location.href, { push: false });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', bindAll);
})();
